import { Button, ButtonTypes, Checkbox, Modal, Table } from "@clintonelec/react-storybook";
import type { Column, WithKey } from "@clintonelec/react-storybook";
import type { UserResource } from "Interfaces";
import { type ChangeEvent, memo, type PropsWithChildren, useState } from "react";
import "Components/Dashboards/SupportDashboard/UserInformation/DeleteAccountModal/DeleteAccountModal.less";
import { useAppDispatch } from "Data/Redux/Store";
import { deleteUserAction } from "Data/Redux/Slices/User";
import { NotificationType, notify } from "src/Notifications";
import { useNavigate } from "react-router-dom";

type DeleteAccountModalProps = {
	selectedUser: UserResource;
};

const hostnamesTableColumns: Column[] = [
	{
		key: "hostname",
		dataIndex: "hostname",
		title: "Hostname",
		sortable: false
	},
	{
		key: "macAddress",
		dataIndex: "macAddress",
		title: "MAC Address",
		sortable: false
	},
	{
		key: "ipv4",
		dataIndex: "ipv4",
		title: "IPv4 Address",
		sortable: false
	},
	{
		key: "ipv6",
		dataIndex: "ipv6",
		title: "IPv6 Address",
		sortable: false
	}
];

function DeleteAccountModal({ children, selectedUser }: PropsWithChildren<DeleteAccountModalProps>) {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [ visible, setVisible ] = useState(false);
	const [ checked, setChecked ] = useState(false);
	const tableData: WithKey[] = selectedUser?.hostnames?.map(({ id, hostname, ipv4, ipv6 }) => ({
		key: id,
		hostname: hostname,
		macAddress: "",
		ipv4: ipv4,
		ipv6: ipv6
	}));

	const handleDeleteClick = () => {
		dispatch(deleteUserAction(selectedUser.id))
			.then(() => {
				setVisible(false);
				navigate(0);
			})
			.catch(() => {
				notify(NotificationType.ERROR, "Error deleting user account.");
			});
	};

	const handleCheckboxChanged = (event: ChangeEvent<HTMLInputElement>) => {
		setChecked(event.currentTarget.checked);
	};

	const handleVisibilityChange = () => {
		setVisible(!visible);
	};

	const modalContent = (
		<div className="delete-account-modal">
			<div className="delete-account-warning">
				<p>Are you sure you want to delete the user?</p>
				<p>Deleting the user will also delete all associated hostnames with this account.</p>
			</div>
			<Table
				columns={ hostnamesTableColumns }
				data={ tableData }
			/>
			<div className="delete-account-footer">
				<Checkbox checked={ checked } onChange={ handleCheckboxChanged }>
					I understand that this can not be undone.
				</Checkbox>
				<div className="delete-account-actions">
					<Button fill type={ ButtonTypes.SECONDARY } onClick={ handleVisibilityChange }>
						Cancel
					</Button>
					<Button type={ ButtonTypes.DANGER } disabled={ !checked } onClick={ handleDeleteClick }>
						Delete
					</Button>
				</div>
			</div>
		</div>
	);

	return (
		<Modal
			title="Confirm Delete User Account"
			modalContent={ modalContent }
			visible={ visible }
			onVisibilityChange={ handleVisibilityChange }
			width={ 750 }
		>
			{ children }
		</Modal>
	);
}

export default memo(DeleteAccountModal);
